import { useNavigate } from 'react-router-dom'
import { Button, IconButton, TableCell, TableRow } from '@/common'
import { TrashIcon } from '@/common/components/icons'
import useI18n from '@/shared/hooks/usei18n'
import { useSimulationWithLiability } from '@/features/cbam-simulations-v2/hooks/useSimulationWithLiabilty'
import { useSession } from '@/shared/hooks/useSession'
import { getSimulationYearTotals } from '@/features/cbam-simulations-v2/pages/cbam-simulation-details/utils/simulationYearTotals'
import { useMemo } from 'react'
import { type Quarter } from '@/config/cbamReportingPeriods'

export default function CbamSimulationRow({
  simulationId,
  onDeleteSimulation
}: {
  simulationId: string
  onDeleteSimulation: (simulationId: string) => void
}) {
  const navigate = useNavigate()
  const { t, numberFormatterNoDecimals, currencyFormatter2Decimals } = useI18n()

  const organizationId = useSession().session.organization.id
  const {
    liabilityData,
    liabilityDataBaseLow,
    liabilityDataHighForScenario,
    simulation,
    importedGoodsExtended
  } = useSimulationWithLiability(organizationId, simulationId)

  const totalVolume = liabilityData?.aggregatedProjection[2026]?.volume || 0
  const totalDirectEmissions = liabilityData?.aggregatedProjection[2026]?.directEmissions || 0
  const totalIndirectEmissions = liabilityData?.aggregatedProjection[2026]?.indirectEmissions || 0
  const totalEmissions = liabilityData?.aggregatedProjection[2026]?.netEmissions || 0

  const quartersWithGoods = useMemo(() => {
    const quarters = new Set<Quarter>()
    for (const good of importedGoodsExtended) {
      if (good.quarter) {
        quarters.add(good.quarter)
      }
    }
    return Array.from(quarters)
  }, [importedGoodsExtended])

  const totals2026 = useMemo(
    () =>
      getSimulationYearTotals(
        liabilityDataBaseLow,
        simulation?.modifiers,
        2026,
        quartersWithGoods,
        liabilityDataHighForScenario
      ),
    [liabilityDataBaseLow, simulation?.modifiers, quartersWithGoods, liabilityDataHighForScenario]
  )

  return (
    <TableRow className="text-sm">
      <TableCell>
        <p>{simulation?.name}</p>
        <p className="text-sm">
          {t('cbamSimulations:cbamSimulationsPage.cbamSimulationsTable.row.goodsCount', {
            count: importedGoodsExtended.length
          })}
        </p>
      </TableCell>
      <TableCell>{liabilityData ? `${numberFormatterNoDecimals(totalVolume)} t` : '-'}</TableCell>
      <TableCell align="right">
        {totalDirectEmissions ? `${numberFormatterNoDecimals(totalDirectEmissions)} tCO₂e` : '-'}
      </TableCell>
      <TableCell align="right">
        {totalIndirectEmissions
          ? `${numberFormatterNoDecimals(totalIndirectEmissions)} tCO₂e`
          : '-'}
      </TableCell>
      <TableCell align="right">
        {totalEmissions ? `${numberFormatterNoDecimals(totalEmissions)} tCO₂e` : '-'}
      </TableCell>
      <TableCell align="right">
        {totals2026.totalLiabilityBase > 0
          ? currencyFormatter2Decimals(totals2026.totalLiabilityBase)
          : '-'}
      </TableCell>
      <TableCell align="right">
        {totals2026.totalLiabilityHigh > 0
          ? currencyFormatter2Decimals(totals2026.totalLiabilityHigh)
          : '-'}
      </TableCell>
      <TableCell align="right">
        {totals2026.totalLiabilityLow > 0
          ? currencyFormatter2Decimals(totals2026.totalLiabilityLow)
          : '-'}
      </TableCell>
      <TableCell>
        <div className="flex gap-2 justify-end">
          <Button onClick={() => navigate(simulationId)} variant="subtle">
            {t('common:actions.view')}
          </Button>
          <IconButton
            variant="txt-danger"
            icon={<TrashIcon />}
            onClick={() => onDeleteSimulation(simulationId)}
          />
        </div>
      </TableCell>
    </TableRow>
  )
}
